// Elveris sign-in for this SPA (client `elveris-webrtc`, elveris-api seed
// 894edea). Authorization code + PKCE (RFC 7636), DPoP-bound tokens
// (RFC 9449) with the code bound to the key via `dpop_jkt` (§10), refresh
// token in the API's HttpOnly `__Host-` cookie — never readable here.
//
// What it is for: GET /account/turn-credentials, which only an end-user
// access token of this client may call. The TURN grant fetcher registers with
// the ICE resolver once a session exists and unregisters on sign-out.
//
// Storage: the DPoP private key is a non-extractable CryptoKey in IndexedDB
// (structured clone), so script can sign with it but never export it. The
// access token lives in memory only. localStorage holds one flag ("a session
// may exist") so a fresh load knows whether a silent refresh is worth a
// request; it holds no credential.
//
// Configuration (build time): VITE_ELVERIS_ISSUER, VITE_ELVERIS_CLIENT_ID,
// VITE_ELVERIS_REDIRECT_URI. Missing any of them = sign-in not configured; no
// value is invented.

import { reactive } from 'vue'

export interface SessionState {
  status: 'not-configured' | 'unknown' | 'signed-out' | 'signed-in' | 'error'
  email: string | null
  error: string | null
}

export const session = reactive<SessionState>({
  status: 'unknown',
  email: null,
  error: null,
})

interface Config {
  issuer: string
  clientId: string
  redirectUri: string
}

interface Discovery {
  authorization_endpoint: string
  token_endpoint: string
  end_session_endpoint?: string
}

/** The token endpoint answered with an OAuth error (as opposed to no answer). */
export class TokenEndpointError extends Error {
  constructor(readonly status: number, readonly code: string) {
    super(`token endpoint: ${code}`)
  }
}

interface TokenResponse {
  access_token: string
  token_type: string
  expires_in: number
  id_token?: string
}

const SESSION_FLAG = 'elveris-webrtc:session'
const PKCE_KEY = 'elveris-webrtc:pkce'
const DB_NAME = 'elveris-webrtc-auth'
const DB_STORE = 'keys'
const DPOP_KEY_ID = 'dpop-es256'
const REFRESH_LOCK = 'elveris-webrtc:refresh'
// Refresh this long before expiry so a request never carries a token that
// expires in flight.
const EXPIRY_MARGIN_MS = 60_000

export function readConfig(env: Record<string, string | undefined>): Config | null {
  const issuer = env['VITE_ELVERIS_ISSUER']
  const clientId = env['VITE_ELVERIS_CLIENT_ID']
  const redirectUri = env['VITE_ELVERIS_REDIRECT_URI']
  if (!issuer || !clientId || !redirectUri) return null
  return { issuer: issuer.replace(/\/+$/, ''), clientId, redirectUri }
}

const config = readConfig(import.meta.env as Record<string, string | undefined>)

// ── encoding / crypto primitives (pure, unit-tested) ────────────────────────

export function base64Url(bytes: Uint8Array): string {
  let binary = ''
  for (const b of bytes) binary += String.fromCharCode(b)
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

function randomToken(bytes = 32): string {
  return base64Url(crypto.getRandomValues(new Uint8Array(bytes)))
}

async function sha256(text: string): Promise<Uint8Array> {
  return new Uint8Array(await crypto.subtle.digest('SHA-256', new TextEncoder().encode(text)))
}

/** RFC 7636 §4.2 S256. */
export async function pkceChallenge(verifier: string): Promise<string> {
  return base64Url(await sha256(verifier))
}

/** RFC 7638 thumbprint of an EC P-256 public JWK (members in lexical order). */
export async function jwkThumbprint(jwk: JsonWebKey): Promise<string> {
  const canonical = JSON.stringify({ crv: jwk.crv, kty: jwk.kty, x: jwk.x, y: jwk.y })
  return base64Url(await sha256(canonical))
}

/** RFC 9449 §4 proof. `htu` without query and fragment (§4.2). */
export async function buildDpopProof(opts: {
  privateKey: CryptoKey
  publicJwk: JsonWebKey
  htm: string
  url: string
  accessToken?: string
  nonce?: string | null
}): Promise<string> {
  const htu = new URL(opts.url)
  htu.search = ''
  htu.hash = ''
  const header = {
    typ: 'dpop+jwt',
    alg: 'ES256',
    jwk: { kty: opts.publicJwk.kty, crv: opts.publicJwk.crv, x: opts.publicJwk.x, y: opts.publicJwk.y },
  }
  const payload: Record<string, string | number> = {
    htm: opts.htm,
    htu: htu.toString(),
    iat: Math.floor(Date.now() / 1000),
    jti: crypto.randomUUID(),
  }
  if (opts.accessToken) payload['ath'] = base64Url(await sha256(opts.accessToken))
  if (opts.nonce) payload['nonce'] = opts.nonce
  const enc = (o: unknown) => base64Url(new TextEncoder().encode(JSON.stringify(o)))
  const signingInput = `${enc(header)}.${enc(payload)}`
  const sig = await crypto.subtle.sign(
    { name: 'ECDSA', hash: 'SHA-256' },
    opts.privateKey,
    new TextEncoder().encode(signingInput),
  )
  return `${signingInput}.${base64Url(new Uint8Array(sig))}`
}

/** True when the server asks for a (new) DPoP nonce (RFC 9449 §8, §9). */
export function wantsDpopNonce(status: number, body: unknown, wwwAuthenticate: string | null): boolean {
  if (status === 400 && typeof body === 'object' && body !== null) {
    return (body as { error?: unknown }).error === 'use_dpop_nonce'
  }
  return status === 401 && /error\s*=\s*"?use_dpop_nonce"?/i.test(wwwAuthenticate ?? '')
}

// ── DPoP key (IndexedDB, non-extractable) ───────────────────────────────────

interface DpopKey {
  privateKey: CryptoKey
  publicJwk: JsonWebKey
}

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 1)
    req.onupgradeneeded = () => req.result.createObjectStore(DB_STORE)
    req.onsuccess = () => resolve(req.result)
    req.onerror = () => reject(req.error)
  })
}

async function idb<T>(mode: IDBTransactionMode, op: (s: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  const db = await openDb()
  try {
    return await new Promise<T>((resolve, reject) => {
      const req = op(db.transaction(DB_STORE, mode).objectStore(DB_STORE))
      req.onsuccess = () => resolve(req.result)
      req.onerror = () => reject(req.error)
    })
  } finally {
    db.close()
  }
}

let dpopKey: Promise<DpopKey> | null = null

function getDpopKey(): Promise<DpopKey> {
  dpopKey ??= (async () => {
    const stored = await idb<CryptoKeyPair | undefined>('readonly', (s) => s.get(DPOP_KEY_ID))
    let pair = stored
    if (!pair) {
      pair = await crypto.subtle.generateKey({ name: 'ECDSA', namedCurve: 'P-256' }, false, ['sign', 'verify'])
      await idb('readwrite', (s) => s.put(pair, DPOP_KEY_ID))
    }
    return { privateKey: pair.privateKey, publicJwk: await crypto.subtle.exportKey('jwk', pair.publicKey) }
  })()
  return dpopKey
}

// ── endpoints ───────────────────────────────────────────────────────────────

let discovery: Promise<Discovery> | null = null

function getDiscovery(cfg: Config): Promise<Discovery> {
  discovery ??= fetch(`${cfg.issuer}/.well-known/openid-configuration`, { cache: 'no-store' }).then(async (r) => {
    if (!r.ok) throw new Error(`discovery HTTP ${r.status}`)
    const d = (await r.json()) as Discovery
    if (!d.authorization_endpoint || !d.token_endpoint) throw new Error('discovery is missing endpoints')
    return d
  })
  discovery.catch(() => {
    discovery = null
  })
  return discovery
}

// ── tokens ──────────────────────────────────────────────────────────────────

let accessToken: string | null = null
let accessTokenExpiresAt = 0
let nonce: string | null = null
let refreshing: Promise<boolean> | null = null

function decodeJwtPayload(jwt: string): Record<string, unknown> | null {
  try {
    const part = jwt.split('.')[1] ?? ''
    const json = atob(part.replace(/-/g, '+').replace(/_/g, '/'))
    return JSON.parse(json) as Record<string, unknown>
  } catch {
    return null
  }
}

/** POST to the token endpoint with a DPoP proof; one retry on a nonce challenge. */
async function tokenRequest(cfg: Config, form: Record<string, string>): Promise<TokenResponse> {
  const { token_endpoint } = await getDiscovery(cfg)
  const key = await getDpopKey()
  const send = async () =>
    fetch(token_endpoint, {
      method: 'POST',
      credentials: 'include', // the refresh token is the API's HttpOnly cookie
      cache: 'no-store',
      headers: {
        'content-type': 'application/x-www-form-urlencoded',
        DPoP: await buildDpopProof({ ...key, htm: 'POST', url: token_endpoint, nonce }),
      },
      body: new URLSearchParams(form).toString(),
    })
  let res = await send()
  nonce = res.headers.get('DPoP-Nonce') ?? nonce
  let body: unknown = await res.json().catch(() => null)
  if (wantsDpopNonce(res.status, body, res.headers.get('WWW-Authenticate'))) {
    res = await send()
    nonce = res.headers.get('DPoP-Nonce') ?? nonce
    body = await res.json().catch(() => null)
  }
  if (!res.ok) {
    throw new TokenEndpointError(res.status, (body as { error?: string } | null)?.error ?? `HTTP ${res.status}`)
  }
  const t = body as TokenResponse
  if (!t?.access_token || t.token_type?.toLowerCase() !== 'dpop') {
    throw new Error('token endpoint did not return a DPoP-bound access token')
  }
  return t
}

function adopt(t: TokenResponse): void {
  const wasSignedIn = session.status === 'signed-in'
  accessToken = t.access_token
  accessTokenExpiresAt = Date.now() + t.expires_in * 1000
  const claims = t.id_token ? decodeJwtPayload(t.id_token) : null
  if (claims && typeof claims['email'] === 'string') session.email = claims['email']
  session.status = 'signed-in'
  session.error = null
  localStorage.setItem(SESSION_FLAG, '1')
  // Only on the transition: a token refresh must not re-trigger a TURN grant
  // fetch (the server allows 30 per user per hour).
  if (!wasSignedIn) {
    onSignedIn?.()
    if (!session.email) void loadEmail()
  }
}

/**
 * elveris-api's authorization_code id_token carries no profile claims (its
 * extra-claims map is empty), so the address comes from /userinfo — the OIDC
 * Core §5.3 source for them. Best-effort: display only.
 */
async function loadEmail(): Promise<void> {
  if (!config) return
  try {
    const res = await authedFetch(`${config.issuer}/userinfo`, { cache: 'no-store', credentials: 'omit' })
    if (!res.ok) return
    const info = (await res.json()) as { email?: unknown }
    if (typeof info.email === 'string' && session.status === 'signed-in') session.email = info.email
  } catch {
    // The header then just says "Signed in".
  }
}

function forget(): void {
  const wasSignedIn = session.status === 'signed-in'
  accessToken = null
  accessTokenExpiresAt = 0
  session.email = null
  localStorage.removeItem(SESSION_FLAG)
  if (wasSignedIn) onSignedOut?.()
}

/**
 * Silent refresh from the cookie. Serialised across tabs (Web Locks) because
 * the server rotates the refresh token and treats a replayed one as theft.
 */
function refresh(cfg: Config): Promise<boolean> {
  refreshing ??= (async () => {
    const run = async () => {
      try {
        adopt(await tokenRequest(cfg, { grant_type: 'refresh_token', client_id: cfg.clientId }))
        return true
      } catch (e) {
        session.error = e instanceof Error ? e.message : String(e)
        if (e instanceof TokenEndpointError && (e.status === 400 || e.status === 401)) {
          // The server rejected the refresh token: the session is over.
          forget()
          session.status = 'signed-out'
        } else {
          // No answer (offline, 5xx): keep the session flag and try again on
          // the next call instead of signing the user out on a blip.
          accessToken = null
          session.status = 'error'
        }
        return false
      }
    }
    return navigator.locks ? navigator.locks.request(REFRESH_LOCK, run) : run()
  })().finally(() => {
    refreshing = null
  })
  return refreshing
}

async function currentAccessToken(cfg: Config): Promise<string | null> {
  if (accessToken && Date.now() < accessTokenExpiresAt - EXPIRY_MARGIN_MS) return accessToken
  return (await refresh(cfg)) ? accessToken : null
}

// ── public API ──────────────────────────────────────────────────────────────

let onSignedIn: (() => void) | null = null
let onSignedOut: (() => void) | null = null

/** Hooks the ICE resolver (or anything else) into session changes. */
export function onSessionChange(signedIn: () => void, signedOut: () => void): void {
  onSignedIn = signedIn
  onSignedOut = signedOut
  if (session.status === 'signed-in') signedIn()
}

/** Call once on the client. Restores a session only if one may exist. */
export async function initSession(): Promise<void> {
  if (!config) {
    session.status = 'not-configured'
    return
  }
  if (localStorage.getItem(SESSION_FLAG) !== '1') {
    session.status = 'signed-out'
    return
  }
  await refresh(config)
}

/** True when this page's origin is the one registered as redirect URI. */
export function onRegisteredOrigin(): boolean {
  return !!config && new URL(config.redirectUri).origin === location.origin
}

export async function signIn(returnTo: string = location.pathname): Promise<void> {
  if (!config) throw new Error('sign-in is not configured')
  if (!onRegisteredOrigin()) {
    // The authorization server only redirects to the registered origin.
    location.assign(new URL(returnTo, new URL(config.redirectUri).origin).toString())
    return
  }
  const { authorization_endpoint } = await getDiscovery(config)
  const key = await getDpopKey()
  const verifier = randomToken(32)
  const state = randomToken(16)
  sessionStorage.setItem(PKCE_KEY, JSON.stringify({ verifier, state, returnTo }))
  const url = new URL(authorization_endpoint)
  url.search = new URLSearchParams({
    response_type: 'code',
    client_id: config.clientId,
    redirect_uri: config.redirectUri,
    scope: 'openid profile email offline_access',
    state,
    code_challenge: await pkceChallenge(verifier),
    code_challenge_method: 'S256',
    dpop_jkt: await jwkThumbprint(key.publicJwk),
  }).toString()
  location.assign(url.toString())
}

/** Handles /callback. Returns where to go next. */
export async function completeSignIn(search: string): Promise<string> {
  if (!config) throw new Error('sign-in is not configured')
  const params = new URLSearchParams(search)
  const saved = sessionStorage.getItem(PKCE_KEY)
  sessionStorage.removeItem(PKCE_KEY)
  const pkce = saved ? (JSON.parse(saved) as { verifier: string; state: string; returnTo: string }) : null
  if (params.get('error')) throw new Error(`authorization: ${params.get('error')}`)
  const code = params.get('code')
  if (!code || !pkce || params.get('state') !== pkce.state) {
    throw new Error('sign-in response does not match this browser session (state)')
  }
  adopt(
    await tokenRequest(config, {
      grant_type: 'authorization_code',
      code,
      redirect_uri: config.redirectUri,
      client_id: config.clientId,
      code_verifier: pkce.verifier,
    }),
  )
  return pkce.returnTo || '/'
}

/**
 * Sign out of THIS app only. The refresh token is in an HttpOnly cookie, so
 * it cannot be sent to /revoke; it is DPoP-bound to this browser's key
 * (elveris-api ELV-235), so discarding the key makes it unusable. The access
 * token is revoked best-effort. (/logout would revoke every session of the
 * user in every Elveris app; /oidc/session/end needs an id_token_hint and an
 * env-level redirect allowlist.)
 */
export async function signOut(): Promise<void> {
  if (!config) return
  const token = accessToken
  if (token) {
    try {
      const key = await getDpopKey()
      const url = `${config.issuer}/revoke`
      await fetch(url, {
        method: 'POST',
        cache: 'no-store',
        headers: {
          'content-type': 'application/x-www-form-urlencoded',
          DPoP: await buildDpopProof({ ...key, htm: 'POST', url, nonce }),
        },
        body: new URLSearchParams({
          token,
          token_type_hint: 'access_token',
          client_id: config.clientId,
        }).toString(),
      })
    } catch {
      // Revocation is best-effort; discarding the key below is what ends it.
    }
  }
  await idb('readwrite', (s) => s.delete(DPOP_KEY_ID))
  dpopKey = null
  nonce = null
  forget()
  session.status = 'signed-out'
  session.error = null
}

/**
 * fetch() with `Authorization: DPoP` + proof (`ath`). One retry on a nonce
 * challenge, one after a refresh when the token was rejected.
 */
export async function authedFetch(url: string, init: RequestInit = {}): Promise<Response> {
  if (!config) throw new Error('sign-in is not configured')
  const cfg = config
  const key = await getDpopKey()
  const method = (init.method ?? 'GET').toUpperCase()
  const send = async (token: string) => {
    const headers = new Headers(init.headers)
    headers.set('Authorization', `DPoP ${token}`)
    headers.set('DPoP', await buildDpopProof({ ...key, htm: method, url, accessToken: token, nonce }))
    return fetch(url, { ...init, method, headers })
  }
  let token = await currentAccessToken(cfg)
  if (!token) throw new Error('not signed in')
  let res = await send(token)
  nonce = res.headers.get('DPoP-Nonce') ?? nonce
  if (res.status === 401) {
    const www = res.headers.get('WWW-Authenticate')
    if (wantsDpopNonce(401, null, www)) {
      res = await send(token)
    } else if ((await refresh(cfg)) && accessToken) {
      token = accessToken
      res = await send(token)
    }
  }
  return res
}

/** The TURN grant (elveris-api GET /account/turn-credentials). */
export async function fetchTurnGrant(signal: AbortSignal): Promise<unknown> {
  if (!config) throw new Error('sign-in is not configured')
  const res = await authedFetch(`${config.issuer}/account/turn-credentials?ttl=600`, {
    signal,
    cache: 'no-store',
    credentials: 'omit',
  })
  if (!res.ok) throw new Error(`HTTP ${res.status}`)
  return res.json()
}
